/**
 * Line properties with what an ABSENT track means (W2-06; DESIGN.md §6.7 "Not set: ..." and Reset property).
 *
 * Every property Phira uses on the line's kind is listed with its state: not set (with the meaning Phira gives an
 * absent list: opacity 0 = the line is invisible but its notes are drawn; a UI mover's element vanishes; scroll
 * speed absent = notes sit on the line...), one keyframe (with its value) or N keyframes / layers. Absent tracks
 * whose meaning is surprising carry a one-click fix (Set 100 %, Set 10); present tracks can be reset (the whole
 * track removed, Core.keys.resetProperty), after which the row explains what "absent" means. Properties Phira
 * ignores on this kind but that hold data are flagged "Phira ignores".
 * Each explanation is shown once (UX-059): a property with its own field above (Length, Thickness...) says only
 * "Not set" here, and at the Full level the "Not set: ..." sentences sit in info popovers.
 */

import type { AppServices } from '../../app/registry/types';
import { Core } from '../../core/commands/index';
import { absentMessageKey, PROP_SPECS } from '../../core/model/props';
import { Units } from '../../core/model/units';
import type { Line, LineKind, LineProp } from '../../core/model/types';
import { useT } from '../../i18n/useT';
import type { DocStore } from '../../state/types';
import type { MessageKey } from '../../i18n/types';
import { Badge, Button, IconButton, InfoPopover } from '../../ui';
import { propLabelKey, unitLabels } from './labels';
import { m } from './inspector.messages';
import { absentFixValue, propRows, trackKeys, type PropRow } from './model';
import { fmtNum } from './parts';
import s from './Inspector.module.css';
import type { EditKit } from './tx';
import { linePropField } from './useFieldProblems';
import type { BadgeProps } from '../../ui/types';

export interface AbsentTrackProps {
  readonly services: AppServices;
  readonly store: DocStore;
  readonly edit: EditKit;
  readonly line: Line;
  readonly kind: LineKind;
  readonly hasNotes: boolean;
  readonly badges: (field: string) => readonly BadgeProps[];
  /** Full level: absent-track explanations go into info popovers. */
  readonly full?: boolean;
  /** Properties with their own field above the list (their absent meaning is explained there). */
  readonly shownAbove?: ReadonlySet<LineProp>;
}

/** Properties that decide the line kind: they change through Type, not Reset. */
const KIND_PROPS: ReadonlySet<LineProp> = new Set<LineProp>(['text', 'paint', 'gif']);

/** The property list of a line. */
export function AbsentTrack(props: AbsentTrackProps) {
  const { store, line, kind } = props;
  const rows = propRows(store.idx, line, kind);
  return (
    <ul className={s.propList} data-testid="property-list">
      {rows.map((row) => (
        <PropertyRow key={row.prop} {...props} row={row} />
      ))}
    </ul>
  );
}

function PropertyRow(props: AbsentTrackProps & { readonly row: PropRow }) {
  const { store, edit, line, kind, row } = props;
  const t = useT();
  const uiMover = line.attachUI !== null;
  const name = t(propLabelKey(row.prop, kind, uiMover));
  const fix = row.layers === 0 ? absentFixValue(row.prop, uiMover, props.hasNotes) : null;

  const absentKey = absentMessageKey(row.prop, kind, uiMover) as MessageKey;
  const absentShort = row.layers === 0 && (props.full === true || props.shownAbove?.has(row.prop) === true);
  let state: string;
  if (row.layers === 0) state = absentShort ? t(m.propNotSet) : t(absentKey);
  else if (row.layers > 1) state = t(m.propLayers, { count: row.layers });
  else if (row.keys === 1) state = t(m.propStatic, { value: staticValue(t, store, line, row.prop, kind) });
  else state = t(m.propAnimated, { count: row.keys });

  const reset = (): void => {
    edit.once({ key: m.txResetProp, params: { prop: name } }, (tx) => {
      const layers = tx.idx.tracksOfLine(line.id).get(row.prop) ?? [];
      // Highest layer first: removing a layer never renumbers the ones still to remove.
      const indexes = layers
        .map((id) => tx.doc.tracks.get(id))
        .map((tr) => (tr?.owner.kind === 'line' ? tr.owner.layer : 0))
        .sort((a, b) => b - a);
      for (const layer of indexes) Core.keys.resetProperty(tx, { line: line.id, prop: row.prop, layer });
    });
  };

  return (
    <li className={s.propItem} data-prop={row.prop} data-help-id={linePropField(row.prop)}>
      <span className={s.propName}>{name}</span>
      <span className={s.propState} data-absent={row.layers === 0 ? 'true' : 'false'}>
        {state}
        {absentShort && props.shownAbove?.has(row.prop) !== true && (
          <>
            {' '}
            <InfoPopover text={{ key: absentKey }} />
          </>
        )}
        {!row.applies && (
          <>
            {' '}
            <Badge kind="phiraIgnores" label={{ key: m.propIgnored }} />
          </>
        )}
        {props.badges(linePropField(row.prop)).map((b, i) => (
          <Badge key={i} {...b} />
        ))}
      </span>
      <span>
        {fix !== null && (
          <Button
            size="sm"
            variant="primary"
            onClick={() => {
              edit.once({ key: m.txAbsentFix, params: { prop: name } }, (tx) => {
                Core.keys.ensureTrack(tx, {
                  owner: { kind: 'line', line: line.id, prop: row.prop, layer: 0 },
                  value: fix,
                });
              });
            }}
          >
            {t(row.prop === 'alpha' ? m.fixAlpha : m.fixSpeed)}
          </Button>
        )}
        {row.layers > 0 && !KIND_PROPS.has(row.prop) && (
          <IconButton icon="RotateCcw" size="sm" label={{ key: m.propReset, params: { prop: name } }} onClick={reset} />
        )}
      </span>
    </li>
  );
}

/** The value of a one-keyframe property in its friendly unit ("100 %", "-250 px", "#ff8000", "Hello"). */
function staticValue(t: ReturnType<typeof useT>, store: DocStore, line: Line, prop: LineProp, kind: LineKind): string {
  const track = store.idx.track(line.id, prop, 0);
  const key = trackKeys(store.doc, store.idx, track)[0];
  if (key === undefined) return '';
  const v = key.vOut;
  if (typeof v === 'string') return v.length > 24 ? `${v.slice(0, 24)}...` : v;
  if (typeof v !== 'number') {
    return `#${v
      .slice(0, 3)
      .map((c) =>
        Math.max(0, Math.min(255, Math.round(c)))
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')}`;
  }
  if (PROP_SPECS[prop].valueType !== 'number') return String(v);
  const unit = Units.unitFor(prop, { kind, uiMover: line.attachUI !== null });
  const shown = Units.toFriendly(prop, v, { kind, uiMover: line.attachUI !== null });
  const label = unitLabels(t, unit).friendly;
  return label === '' ? fmtNum(shown, 3) : `${fmtNum(shown, 3)} ${label}`;
}
